import { useCallback, useEffect, useState } from "react";
import { FileUp, Loader2, Paperclip, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip, Empty, Err, Panel } from "@/components/community/kit";
import { allows, day } from "@/lib/community";

export const Docs = ({ community }) => {
  const cid = community.id;
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [visibility, setVisibility] = useState("members");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const canLeadership = allows(community, "moderate", "full") || community.platform_admin;

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/communities/${cid}/documents`);
      setData(res);
    } catch (e) { setError(errMsg(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const pick = async (chosen) => {
    if (!chosen) return;
    setBusy("upload"); setError("");
    try {
      const form = new FormData();
      form.append("file", chosen);
      const { data: res } = await api.post(`/communities/${cid}/attachments`, form);
      setFile(res.attachment);
      if (!title) setTitle(res.attachment.name.replace(/\.[^.]+$/, ""));
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save"); setError("");
    try {
      await api.post(`/communities/${cid}/documents`, { title, file, visibility, note: "" });
      setTitle(""); setFile(null); setVisibility("members");
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const remove = async (id) => {
    setBusy(id);
    try { await api.delete(`/communities/${cid}/documents/${id}`); await load(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-gold" />;

  return (
    <div data-testid="community-docs" className="space-y-4">
      <Err testId="docs-error">{error}</Err>

      <Panel testId="docs-upload" title="Add to the shelf"
        sub="By-laws, meeting minutes, rate sheets, airport permits — anything the members need.">
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <label data-testid="docs-file-label"
              className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-ink-600 px-3.5 text-sm text-slate-300 hover:border-gold/40">
              {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
              {file ? file.name : "Choose a file"}
              <input data-testid="docs-file" type="file" className="hidden"
                onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            <Input data-testid="docs-title" placeholder="What is it called?" value={title}
              onChange={(e) => setTitle(e.target.value)} />
          </div>
          {canLeadership && (
            <select data-testid="docs-visibility" value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="h-10 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
              <option value="members">Every member can see it</option>
              <option value="leadership">Leadership only</option>
            </select>
          )}
          <Button data-testid="docs-save" onClick={save}
            disabled={!file || title.trim().length < 2 || busy === "save"}>
            {busy === "save" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Put it on the shelf
          </Button>
        </div>
      </Panel>

      {data.documents.length === 0 ? (
        <Empty testId="docs-empty">Nothing on the shelf yet.</Empty>
      ) : (
        <Panel testId="docs-list">
          <ul className="divide-y divide-ink-600">
            {data.documents.map((d) => (
              <li key={d.id} data-testid={`doc-row-${d.id}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3">
                <a href={d.file?.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate text-sm text-slate-100 hover:text-gold">
                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-slate-500" /> {d.title}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-slate-500">
                    {d.uploaded_by} · {day(d.created_at)}
                  </p>
                </a>
                <div className="flex items-center gap-2">
                  {d.visibility === "leadership" && (
                    <Chip className="border-violet-500/40 bg-violet-500/10 text-violet-300">
                      leadership
                    </Chip>
                  )}
                  <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d.id)}
                    className="text-slate-500 hover:text-red-300">
                    {busy === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
};
